import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Game, Card, PlayerMeta } from '@src/lib/types';

import { merge } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { GameState } from '@src/state/slices/types';

const initialState: GameState = {
  games: {},
  chat: {},
  currentGameId: null,
  watchingGameId: null,
  playerMeta: null,
  eventQueue: [],
};

export const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    setPlayerMeta: (state, action: PayloadAction<PlayerMeta>) => {
      state.playerMeta = action.payload;
    },
    cardsUpdated: (
      state,
      action: PayloadAction<{
        playerId: string;
        cards: Card[];
        gameId: string;
      }>,
    ) => {
      const game = state.games[action.payload.gameId];
      if (!game) state.games[action.payload.gameId] = {} as Game;
      game.cards = action.payload.cards;
    },
    updatePlayerChoosingColor: (
      state,
      action: PayloadAction<{
        gameId: string;
        playerId: string;
        playerIndex: number;
      }>,
    ) => {
      const game = state.games[action.payload.gameId];
      if (!game) return;

      if (game.gameId !== state.currentGameId) return;
      game.playerChoosingColor = action.payload.playerId;
    },
    handleNewChat: (
      state,
      action: PayloadAction<{
        gameId: string;
        playerName: string;
        message: string;
        playerId: string;
      }>,
    ) => {
      const game = state.games[action.payload.gameId];
      if (!game) return;

      if (!state.chat[action.payload.gameId])
        state.chat[action.payload.gameId] = [];
      state.chat[action.payload.gameId].push({
        playerName: action.payload.playerName,
        message: action.payload.message,
        playerId: action.payload.playerId,
      });
    },
    gameUpdated: (state, action: PayloadAction<Game>) => {
      const game = state.games[action.payload.gameId];

      const isPlayersTurn = () => {
        if (!game)
          return action.payload.isPlayersTurn
            ? action.payload.isPlayersTurn
            : false;
        if (typeof action.payload.currentPlayerTurn === 'number')
          return action.payload.currentPlayerTurn === game.playerIndex;
        if (action.payload.currentPlayerTurn === game.playerIndex) return true;
        if (typeof game.isPlayersTurn === 'boolean') return game.isPlayersTurn;
        return false;
      };

      if (
        // if just terminated
        action.payload.terminated &&
        !state.games[action.payload.gameId]?.terminated &&
        !action.payload.gameEnded &&
        state.currentGameId === action.payload.gameId
      ) {
        // push game terminated event
        state.eventQueue.push({
          message: 'Game terminated',
          showToast: true,
          sound: 'error',
          id: uuidv4(),
        });
      } else if (
        // game just ended
        action.payload.terminated &&
        !state.games[action.payload.gameId]?.terminated &&
        action.payload.gameEnded &&
        state.currentGameId === action.payload.gameId
      ) {
        // push game over event
        state.eventQueue.push({
          message: 'Game over',
          showToast: true,
          sound: 'gameOver',
          id: uuidv4(),
        });

        // if winner is player, push additional event
        if (action.payload.gameWinner === state.playerMeta?.playerId) {
          state.eventQueue.push({
            message: 'You won!',
            showToast: true,
            sound: 'gameWin',
            id: uuidv4(),
          });
        }
      }

      // merge(state.games[action.payload.gameId], action.payload)
      state.games[action.payload.gameId] = merge(
        state.games[action.payload.gameId],
        action.payload,
        { isPlayersTurn: isPlayersTurn() },
      );

      // overwrite players
      if (action.payload.players) {
        console.log(action.payload.players);
        state.games[action.payload.gameId].players = action.payload.players;
      }
    },
    gameEnded: (
      state,
      action: PayloadAction<{
        gameId: string;
        playerId: string;
        playerIndex: number;
      }>,
    ) => {
      const game = state.games[action.payload.gameId];
      if (!game) return;

      game.playerChoosingColor = null;
      game.currentPlayerTurn = null;
      game.currentCard = null;
      game.currentColor = null;
      game.cards = [];
      game.players = [];
      game.gameStart = false;
      game.isPlayersTurn = false;
    },
    onJoinedGame: (
      state,
      action: PayloadAction<{
        gameId: string;
        playerId: string;
        playerIndex: number;
      }>,
    ) => {
      state.currentGameId = action.payload.gameId;

      const game = state.games[action.payload.gameId];
      if (!game) {
        state.games[action.payload.gameId] = {
          playerIndex: action.payload.playerIndex,
        } as Game;
        return;
      }

      game.playerIndex = action.payload.playerIndex;
    },
    onPlayerIndexChanged: (
      state,
      action: PayloadAction<{
        gameId: string;
        playerId: string;
        newIndex: number;
      }>,
    ) => {
      const game = state.games[action.payload.gameId];
      if (!game) return;
      if (!state.playerMeta) return;
      if (action.payload.playerId !== state.playerMeta.playerId) return;

      game.playerIndex = action.payload.newIndex;
    },
    pushEventQueue: (
      state,
      action: PayloadAction<{
        message: string;
        showToast: boolean;
        sound: string | null;
      }>,
    ) => {
      state.eventQueue.push({
        ...action.payload,
        id: uuidv4(),
      });
    },
    popEventQueue: (state) => {
      // first in first out, so use shift
      state.eventQueue.shift();
    },
    clearGamesState: (state) => {
      state.games = {};
      state.chat = {};
      state.currentGameId = null;
      state.watchingGameId = null;
      state.playerMeta = null;
      state.eventQueue = [];
    },
    clearEventQueue: (state) => {
      state.eventQueue = [];
    },
    onKick: (state, action: PayloadAction<{ gameId: string }>) => {
      if (state.currentGameId === action.payload.gameId) {
        state.currentGameId = null;
        state.watchingGameId = null;
        state.chat = {};

        const game = state.games[action.payload.gameId];
        if (!game) return;

        if (game.wager && game.wager > 0) {
          state.eventQueue.push({
            message:
              'Removed from game - your $GOLD balance or allowance was insufficient',
            showToast: true,
            sound: 'error',
            id: uuidv4(),
          });
        } else {
          state.eventQueue.push({
            message: 'Removed from game',
            showToast: true,
            sound: 'error',
            id: uuidv4(),
          });
        }
      }
    },
    clearCurrentGame: (state) => {
      state.currentGameId = null;
      state.watchingGameId = null;
    },
    clearGameDetails: (state) => {
      Object.keys(state.games).forEach((gameId) => {
        if (gameId === state.currentGameId) return;
        delete state.games[gameId];
      });
    },
    onWatchingGame: (state, action: PayloadAction<{ gameId: string }>) => {
      state.watchingGameId = action.payload.gameId;
      state.currentGameId = action.payload.gameId;
    },
  },
});

export default gameSlice.reducer;

export const {
  setPlayerMeta,
  cardsUpdated,
  updatePlayerChoosingColor,
  handleNewChat,
  gameUpdated,
  gameEnded,
  onJoinedGame,
  onPlayerIndexChanged,
  pushEventQueue,
  popEventQueue,
  clearGamesState,
  clearEventQueue,
  onKick,
  clearGameDetails,
  onWatchingGame,
  clearCurrentGame,
} = gameSlice.actions;
